<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    persistent
    max-width="500"
    class="upload-feedback-modal"
  >
    <v-card class="card-soft">
      <v-card-title class="d-flex align-center justify-space-between">
        {{ $t('upload.feedback.title') }}
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="handleSkip"
          :disabled="loading"
        />
      </v-card-title>

      <v-card-text>
        <p class="text-body-1 mb-4">{{ $t('upload.feedback.description') }}</p>
        <p class="optional-marker">
          {{ $t('upload.feedback.optionalHint') }}
        </p>

        <v-form @submit.prevent="handleSubmit">
          <!-- Expectations (optional — personalises the result, never blocks it) -->
          <v-textarea
            v-model="feedback.expectations"
            :label="$t('upload.feedback.expectations.label')"
            :placeholder="$t('upload.feedback.expectations.placeholder')"
            :hint="$t('upload.feedback.expectations.hint')"
            rows="3"
            class="mb-4"
            :disabled="loading"
          ></v-textarea>

          <!-- Usage Purpose (optional) -->
          <v-select
            v-model="selectedPurposeTitle"
            :items="purposeOptionTitles"
            :label="$t('upload.feedback.purpose.label')"
            :hint="$t('upload.feedback.purpose.hint')"
            clearable
            class="mb-4"
            :disabled="loading"
          />

          <!-- Privacy Policy Consent -->
          <p class="text-body-2 text-grey-darken-1">
            {{ $t('upload.feedback.privacy.text') }}
            <router-link to="/datenschutz" target="_blank">
              {{ $t('upload.feedback.privacy.link') }}
            </router-link>
          </p>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn
          variant="text"
          @click="handleSkip"
          :disabled="loading"
        >
          {{ $t('upload.feedback.skip') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="handleSubmit"
          :loading="loading"
          :disabled="loading"
        >
          {{ $t('upload.feedback.submit') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { trackUploadFeedback } from '@/utils/analytics'
import { getClientId } from '@/utils/clientId'
import { buildUploadFeedbackPayload, getUploadPurposeOptions } from '@/utils/uploadFeedback.mjs'
import { readStudioHandoff } from '@/utils/studioHandoff.mjs'
import axios from 'axios'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  filename: {
    type: String,
    required: false,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'submit', 'skip'])

const loading = ref(false)

const feedback = ref({
  expectations: '',
})
const selectedPurposeTitle = ref('')

const purposeOptions = computed(() => getUploadPurposeOptions(t))
const purposeOptionTitles = computed(() => purposeOptions.value.map((option) => option.title))

const resetForm = () => {
  feedback.value = {
    expectations: '',
  }
  selectedPurposeTitle.value = ''
}

// When the modal opens, replay a captured studio handoff (per v2 spec §12).
// A user who hit the Studio paywall before signing up gets back to this
// modal with their expectations + purpose pre-filled, so they don't have to
// type them again.
const tryReplayHandoff = () => {
  try {
    const handoff = readStudioHandoff({ peek: true })
    const expectations = handoff?.userIntent?.expectations
    const purposeKey = handoff?.userIntent?.purpose
    if (expectations) feedback.value.expectations = expectations
    if (purposeKey) {
      const match = purposeOptions.value.find((opt) => opt.value === purposeKey)
      if (match) selectedPurposeTitle.value = match.title
    }
  } catch (_) { /* best-effort */ }
}

watch(
  () => props.modelValue,
  (isOpen, wasOpen) => {
    if (!isOpen && wasOpen) {
      resetForm()
    } else if (isOpen && !wasOpen) {
      tryReplayHandoff()
    }
  }
)

const handleSubmit = async () => {
  loading.value = true
  try {
    const selectedPurpose = purposeOptions.value.find(
      (option) => option.title === selectedPurposeTitle.value,
    ) || null
    const payload = buildUploadFeedbackPayload(feedback.value, selectedPurpose)
    // Get client ID for tracking
    const clientId = getClientId()

    // Send feedback to backend with filename and clientId
    await axios.post('/api/feedback', {
      ...payload,
      clientId,
      ...(props.filename ? { filename: props.filename } : {})
    })

    // Track feedback submission with clientId
    trackUploadFeedback({
      ...payload,
      clientId,
      ...(props.filename ? { filename: props.filename } : {}),
      skipped: false
    })

    emit('submit', payload)
  } catch (error) {
    console.error('Failed to store feedback:', error)
  } finally {
    loading.value = false
  }
}

const handleSkip = () => {
  trackUploadFeedback({ skipped: true })
  emit('skip')
}
</script>

<style scoped>
.upload-feedback-modal {
  backdrop-filter: blur(4px);
}

.optional-marker {
  margin: 0 0 16px;
  color: rgb(var(--v-theme-on-surface-variant, var(--v-theme-primary)));
  opacity: 0.75;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
}

:deep(.v-card) {
  border-radius: 12px;
}

:deep(.v-card-title) {
  padding: 16px 24px;
  font-size: var(--font-size-h5);
  font-weight: var(--font-weight-semibold);
}

:deep(.v-card-text) {
  padding: 24px;
  padding-top: 0;
  font-size: var(--font-size-body-1);
}

@media (max-width: 600px) {
  :deep(.v-card-title) {
    font-size: var(--font-size-h6);
  }

  :deep(.v-card-text) {
    font-size: var(--font-size-body-2);
  }

  :deep(.v-btn) {
    font-size: var(--font-size-body-2);
  }

  :deep(.v-label),
  :deep(.v-field__input),
  :deep(.v-select__selection),
  :deep(.v-textarea textarea),
  :deep(.v-messages),
  :deep(.v-field__hint) {
    font-size: calc(var(--font-size-body-2) - 0.125rem) !important;
  }

  :deep(.text-body-1) {
    font-size: calc(var(--font-size-body-2) - 0.125rem) !important;
  }
}
</style> 
